import moment from 'moment';
import { useContext, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Line } from 'react-chartjs-2';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';

import { useTranslation } from 'react-i18next';
import {
  CategoryScale,
  Chart,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Title,
} from 'chart.js';
import {
  FilterContext,
  Spinner,
  Flex,
  ColorContext,
  getPeriodRangeByPeriodType,
  DatePeriodTypesEnum,
} from 'dash-ui';
import {
  useGetBrokerViewedPortfoliosQuery,
  useGetPortfoliosQuery,
} from 'gql/generated.graphql';

import PortfolioViewsPlaceholder from 'pages/Dashboard/components/PortfolioViewsWidget/components/PortfolioViewsPlaceholder/PortfolioViewsPlaceholder';
import {
  BrokerPortfolioStatusesEnum,
  BrokerPortfolioTypesEnum,
} from 'dash-common';

Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Title
);

const ChatContainer = styled(Flex)`
  min-width: 564px;
  @media ${({ theme }) => theme.devices.mobile} {
    overflow-x: scroll;
  }
`;

function PortfolioViewsChart() {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { state: filterState } = useContext(FilterContext);
  const { colorMap } = useContext(ColorContext);
  const { dateRange, portfolioIds = [] } = filterState;
  const { data: fetchData, loading } = useGetBrokerViewedPortfoliosQuery({
    fetchPolicy: 'network-only',
    variables: {
      pagination: {
        allowLimit: false,
      },
      filter: {
        range: dateRange
          ? {
              startDate: dateRange?.startDate,
              endDate: dateRange?.endDate,
            }
          : getPeriodRangeByPeriodType(DatePeriodTypesEnum.CURRENT_WEEK),
        portfolioIds,
      },
      sort: [{ fieldName: 'createdAt', sortOrder: 1 }],
    },
    skip: portfolioIds.length === 0 || dateRange === undefined,
  });
  const list = useMemo(
    () => fetchData?.getBrokerViewedPortfolios?.collection || [],
    [fetchData]
  );

  const { data: dataPortfolio, loading: portfolioLoading } =
    useGetPortfoliosQuery({
      fetchPolicy: 'network-only',
    });
  const { portfolios, portfolioCount } = useMemo(
    () => ({
      portfolios: dataPortfolio?.getPortfolios.collection || [],
      portfolioCount: dataPortfolio?.getPortfolios.totalCount || 0,
    }),
    [dataPortfolio]
  );

  const { suggestedMax, ...data } = useMemo(() => {
    const labelMoments = uniqBy(
      list.map(({ createdAt }) => moment(createdAt)),
      (v) => v.format('MMM/DD')
    );
    let suggestedMax = 0;

    const dataSetsGroup = groupBy(list, 'portfolio.property._id');
    const datasets = Object.keys(dataSetsGroup).map((key) => {
      const portfolio = dataSetsGroup[key][0].portfolio;
      const views = dataSetsGroup[key];
      const data = labelMoments.map(
        (a) =>
          views.filter((item) => {
            const b = moment(item.createdAt);
            return (
              a.get('year') === b.get('year') &&
              a.get('month') === b.get('month') &&
              a.get('day') === b.get('day')
            );
          }).length
      );

      const getLabel = (portfolio, type: BrokerPortfolioTypesEnum) => {
        if (type === BrokerPortfolioTypesEnum.ITEM) {
          let text = portfolio?.property.name;
          if (portfolio?.portfolioFolder)
            text = portfolio?.portfolioFolder.name + ' ' + text;
          return text;
        }
        if (type === BrokerPortfolioTypesEnum.FOLDER) {
          return portfolio?.name;
        }
        return '';
      };

      const color = colorMap[portfolio?._id || ''];
      const label = getLabel(
        portfolio,
        portfolio?.type as BrokerPortfolioTypesEnum
      );
      const opacity = Math.floor(Math.random() * 50) / 100 + 50;
      suggestedMax = Math.max(...data, suggestedMax);

      return {
        label: label,
        data: data,
        borderColor: color,
        backgroundColor: theme.colors.setOpacity(color, opacity),
        fill: true,
      };
    });

    return {
      labels: labelMoments.map((value) => value.format('MMM/DD')),
      datasets,
      suggestedMax,
    };
  }, [list, colorMap, theme]);

  if (loading && portfolioLoading) {
    return <Spinner fullContent />;
  }

  if (portfolioCount === 0) {
    return (
      <PortfolioViewsPlaceholder
        message={t(
          'pages:dashboardPage:portfoliosWidget:noPortfoliosPlaceholderSubTitle'
        )}
        buttonPlaceholder={
          t(
            'pages:dashboardPage:portfoliosWidget:noPortfoliosPlaceholderButtonLabel'
          ) || ''
        }
      />
    );
  }

  if (
    portfolios.filter((p) => p.status === BrokerPortfolioStatusesEnum.PUBLIC)
      .length === 0
  ) {
    return (
      <PortfolioViewsPlaceholder
        message={t(
          'pages:dashboardPage:portfoliosWidget:noPublicPortfoliosPlaceholderSubTitle'
        )}
        buttonPlaceholder={
          t(
            'pages:dashboardPage:portfoliosWidget:noPublicPortfoliosPlaceholderButtonLabel'
          ) || ''
        }
      />
    );
  }

  if (list.length === 0) {
    return (
      <PortfolioViewsPlaceholder
        message={t(
          'pages:dashboardPage:portfoliosWidget:noPortfoliosPlaceholderMatches'
        )}
      />
    );
  }

  return (
    <ChatContainer w="100%">
      <Line
        data={data}
        options={{
          responsive: true,
          aspectRatio: 2,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
            },
            tooltip: {
              mode: 'index',
            },
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
          },
          scales: {
            y: {
              suggestedMin: 0,
              suggestedMax: Math.max(
                suggestedMax + Math.round(suggestedMax * 0.3),
                2
              ),
            },
          },
        }}
      />
    </ChatContainer>
  );
}

export default PortfolioViewsChart;
